import { useState, useEffect, useCallback } from "react";
import { Search, MoreVertical, Eye, Pencil, Trash2, Filter, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import ConfirmDialog from "./ConfirmDialog";
import AddVehicleModal from "./AddVehicleModal";
import { useAuth } from "@/lib/auth";
import api, { extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";


const DEFAULT_PAGE_SIZE = 50;

const VehicleManagement = ({ orgId, loadingOrg }) => {
  const { orgId: sessionOrgId, can } = useAuth();
  const canCreateVehicles = can("VEHICLES.CREATE");
  const canUpdateVehicles = can("VEHICLES.UPDATE");
  const canDeleteVehicles = can("VEHICLES.DELETE");
  const canEditVin = can("VEHICLES.EDIT_VIN");
  const effectiveOrgId = orgId || sessionOrgId || null;
  const [search, setSearch] = useState("");
  const [vehicleCategory, setVehicleCategory] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [openVehicleModal, setOpenVehicleModal] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewVehicle, setViewVehicle] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editVehicleId, setEditVehicleId] = useState(null);
  const [editForm, setEditForm] = useState({
    vehicle_category: "EMERGENCY_VEHICLE",
    vin: "",
    registration_number: "",
    make: "",
    model: "",
    year: "",
    vehicle_type: "",
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    const params = { page: page || 1, page_size: pageSize || DEFAULT_PAGE_SIZE };

    // organization filter precedence
    if (organizationFilter) {
      params.organization_id = organizationFilter;
    } else if (effectiveOrgId) {
      params.organization_id = effectiveOrgId;
    }

    if (vehicleCategory) params.vehicle_category = vehicleCategory;
    if (isActiveFilter !== "") params.is_active = isActiveFilter === "true";
    if (search) params.search = search;
    if (stateFilter) params.state = stateFilter;
    if (cityFilter) params.city = cityFilter;
    if (districtFilter) params.district = districtFilter;

    try {
      const { data } = await api.get("/vehicles", { params });
      const list = Array.isArray(data?.data) ? data.data : data?.data || [];
      setVehicles(list);
    } catch {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveOrgId, page, pageSize, organizationFilter, vehicleCategory, isActiveFilter, search, stateFilter, cityFilter, districtFilter]);

  useEffect(() => {
    if (effectiveOrgId && loadingOrg) return;
    fetchVehicles();
  }, [effectiveOrgId, loadingOrg, fetchVehicles]);

  const filteredVehicles = vehicles.filter((vehicle) =>
    [
      vehicle.registration_number,
      vehicle.vehicle_no,
      vehicle.vehicleNo,
      vehicle.vin,
      vehicle.vehicle_type,
      vehicle.type,
      vehicle.make,
      vehicle.model,
      vehicle.year,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const handleView = async (vehicleId) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewVehicle(null);
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}`);
      setViewVehicle(data?.data || data || null);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load vehicle.");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleEditOpen = async (vehicleId) => {
    setEditOpen(true);
    setEditLoading(true);
    setEditVehicleId(vehicleId);
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}`);
      const v = data?.data || data || {};
      setEditForm({
        vehicle_category: v.vehicle_category || "EMERGENCY_VEHICLE",
        vin: v.vin || "",
        registration_number: v.registration_number || "",
        make: v.make || "",
        model: v.model || "",
        year: v.year ? String(v.year) : "",
        vehicle_type: v.vehicle_type || "",
      });
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load vehicle for edit.");
      setEditOpen(false);
      setEditVehicleId(null);
    } finally {
      setEditLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editVehicleId) return;
    const parsedYear = parseInt(editForm.year, 10);
    if (editForm.year.trim() && Number.isNaN(parsedYear)) {
      toast.error("Year must be a valid number.");
      return;
    }

    const payload = {
      vehicle_category: editForm.vehicle_category.trim(),
      vin: editForm.vin.trim(),
      registration_number: editForm.registration_number.trim(),
      make: editForm.make.trim(),
      model: editForm.model.trim(),
      vehicle_type: editForm.vehicle_type.trim(),
      ...(editForm.year.trim() ? { year: parsedYear } : {}),
    };

    setEditSubmitting(true);
    try {
      await api.patch(`/vehicles/${editVehicleId}`, payload);
      toast.success("Vehicle updated successfully.");
      setEditOpen(false);
      setEditVehicleId(null);
      await fetchVehicles();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to update vehicle.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/vehicles/${deleteTarget.id}`);
      toast.success("Vehicle deleted successfully.");
      setDeleteOpen(false);
      setDeleteTarget(null);
      await fetchVehicles();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to delete vehicle.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 h-full">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search vehicle..."
            className="pl-10 h-11"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-11 bg-slate-900 text-white hover:bg-slate-800" data-testid="vehicle-filter-btn">
              <Filter className="h-4 w-4" />
              Filter
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[320px] p-4">
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Category</Label>
                <Select value={vehicleCategory} onValueChange={(v) => setVehicleCategory(v === "__any" ? "" : v)}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Any</SelectItem>
                    <SelectItem value="USER_VEHICLE">USER_VEHICLE</SelectItem>
                    <SelectItem value="EMERGENCY_VEHICLE">EMERGENCY_VEHICLE</SelectItem>
                  </SelectContent>
                </Select>
              </div>

          <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-black uppercase tracking-widest sts-pds text-slate-600 ml-1">Status</Label>
                <Select  value={isActiveFilter} onValueChange={(v) => setIsActiveFilter(v === "__any" ? "" : v)}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Any</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">State</Label>
                <Input placeholder="State" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} />
              </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">City</Label>
                <Input placeholder="City" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} />
              </div>

              <div>
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">District</Label>
                <Input placeholder="District" value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} />
              </div>
          </div>

              

              {!effectiveOrgId && (
                <div>
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Organization ID</Label>
                  <Input placeholder="organization_id (optional)" value={organizationFilter} onChange={(e) => setOrganizationFilter(e.target.value)} />
                </div>
              )}

              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => { setPage(1); }}>
                  Apply
                </Button>
                <Button variant="ghost" className="flex-1" onClick={() => { setVehicleCategory(""); setIsActiveFilter(""); setStateFilter(""); setCityFilter(""); setDistrictFilter(""); setOrganizationFilter(""); setPage(1); }}>
                  Clear
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant="outline"
          className="h-11 bg-slate-900 text-white hover:bg-slate-800"
          data-testid="vehicle-add-btn"
          onClick={() => setOpenVehicleModal(true)}
          disabled={!canCreateVehicles}
          title={!canCreateVehicles ? "You do not have permission to add vehicles." : undefined}
        >
          <Plus className="h-4 w-4" />
          Add Vehicle
        </Button>
      </div>

      <div className="rounded-xl border overflow-hidden internal-height-1">
        <table className="w-full">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Sr No.</th>
              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Vehicle No.</th>
              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Vehicle Type</th>
              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Make</th>
              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Model</th>
              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Year</th>
              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Status</th>
              <th className="px-5 py-3 text-center text-left font-medium uppercase text-xs">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-5 py-6 text-center text-sm text-slate-500">Loading vehicles…</td>
              </tr>
            )}

            {!loading && filteredVehicles.map((vehicle, index) => (
              <tr key={vehicle.id} className="border-t hover:bg-slate-50">
                <td className="px-5 py-4 text-left font-medium uppercase text-xs">{index + 1}</td>
                <td className="px-5 py-4 text-left font-medium uppercase text-xs">{vehicle.registration_number || vehicle.vehicle_no || vehicle.vehicleNo || vehicle.vin || "-"}</td>
                <td className="px-5 py-4">{vehicle.vehicle_type || vehicle.type || "-"}</td>
                <td className="px-5 py-4 text-left font-medium uppercase text-xs">{vehicle.make || "-"}</td>
                <td className="px-5 py-4 text-left font-medium uppercase text-xs">{vehicle.model || "-"}</td>
                <td className="px-5 py-4 text-left font-medium uppercase text-xs">{vehicle.year || "-"}</td>
                <td className="px-5 py-4 text-left font-medium uppercase text-xs">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${(vehicle.status || vehicle.state) === "Available"
                      ? "bg-green-100 text-green-700"
                      : (vehicle.status || vehicle.state) === "Busy"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                      }`}
                  >
                    {vehicle.status || vehicle.state || "-"}
                  </span>
                </td>
                <td className="text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical size={18} />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleView(vehicle.id); }}>
                        <Eye className="mr-2 h-4 w-4" />
                        View
                      </DropdownMenuItem>

                      {canUpdateVehicles ? (
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleEditOpen(vehicle.id); }}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                      ) : null}

                      {canDeleteVehicles ? (
                        <DropdownMenuItem
                          className="text-red-600"
                          onSelect={(e) => {
                            e.preventDefault();
                            setDeleteTarget(vehicle);
                            setDeleteOpen(true);
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}

            {!loading && filteredVehicles.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-6 text-center text-sm text-slate-500">No vehicles found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddVehicleModal
        open={openVehicleModal}
        onOpenChange={setOpenVehicleModal}
        organizationId={orgId}
        onCreated={fetchVehicles}
      />

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle>Vehicle Details</DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <p><span className="font-semibold">Registration:</span> {viewVehicle?.registration_number || "-"}</p>
              <p><span className="font-semibold">VIN:</span> {viewVehicle?.vin || "-"}</p>
              <p><span className="font-semibold">Make:</span> {viewVehicle?.make || "-"}</p>
              <p><span className="font-semibold">Model:</span> {viewVehicle?.model || "-"}</p>
              <p><span className="font-semibold">Year:</span> {viewVehicle?.year || "-"}</p>
              <p><span className="font-semibold">Type:</span> {viewVehicle?.vehicle_type || "-"}</p>
              <p><span className="font-semibold">Status:</span> {viewVehicle?.status || "-"}</p>
              <p><span className="font-semibold">Category:</span> {viewVehicle?.vehicle_category || "-"}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit Vehicle</DialogTitle>
          </DialogHeader>

          {editLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Vehicle Category</p>
                  <Input value={editForm.vehicle_category} disabled />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">VIN</p>
                  <Input
                    value={editForm.vin}
                    onChange={(e) => setEditForm((p) => ({ ...p, vin: e.target.value }))}
                    readOnly={!canEditVin}
                    aria-readonly={!canEditVin}
                    className={!canEditVin ? "bg-slate-100 cursor-not-allowed" : undefined}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-slate-500">Registration Number</p>
                <Input value={editForm.registration_number} onChange={(e) => setEditForm((p) => ({ ...p, registration_number: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Make</p>
                  <Input value={editForm.make} onChange={(e) => setEditForm((p) => ({ ...p, make: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Model</p>
                  <Input value={editForm.model} onChange={(e) => setEditForm((p) => ({ ...p, model: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Year</p>
                  <Input value={editForm.year} onChange={(e) => setEditForm((p) => ({ ...p, year: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Vehicle Type</p>
                  <Input value={editForm.vehicle_type} onChange={(e) => setEditForm((p) => ({ ...p, vehicle_type: e.target.value }))} />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
                <Button onClick={handleUpdate} disabled={editSubmitting} className="bg-slate-900 text-white hover:bg-slate-800">
                  {editSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete vehicle?"
        description={`This will permanently delete ${deleteTarget?.registration_number || "this vehicle"}.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteSubmitting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default VehicleManagement;